import { useQuery } from "@tanstack/react-query";
import type { Bundle, Category, Product } from "@aurelia/types";
import { apiFetch } from "../../lib/api-client";

interface ProductListResponse {
  items: Product[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ProductFilter {
  categorySlug?: string;
  search?: string;
  sort?: "newest" | "price-asc" | "price-desc" | "bestselling";
  pageSize?: number;
  includeRatings?: boolean;
  // Admin-curated merchandising sections — falls back server-side to real
  // sales/recency data when nothing has been curated yet.
  onlyNewArrivals?: boolean;
  onlyBestSellers?: boolean;
}

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: () => apiFetch<{ categories: Category[] }>("/categories").then((r) => r.categories)
  });
}

export function useBundles() {
  return useQuery({
    queryKey: ["bundles"],
    queryFn: () => apiFetch<{ bundles: Bundle[] }>("/bundles").then((r) => r.bundles)
  });
}

function toQueryString(filter: ProductFilter) {
  const params = new URLSearchParams();
  if (filter.categorySlug) params.set("category", filter.categorySlug);
  if (filter.search) params.set("search", filter.search);
  if (filter.sort) params.set("sort", filter.sort);
  if (filter.pageSize) params.set("pageSize", String(filter.pageSize));
  if (filter.includeRatings) params.set("includeRatings", "true");
  if (filter.onlyNewArrivals) params.set("onlyNewArrivals", "true");
  if (filter.onlyBestSellers) params.set("onlyBestSellers", "true");
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function useProducts(filter: ProductFilter = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["products", filter],
    queryFn: () => apiFetch<ProductListResponse>(`/products${toQueryString(filter)}`),
    enabled: options.enabled ?? true
  });
}

export type ProductBySlugResult = { product: Product; redirect?: undefined } | { product?: undefined; redirect: string };

// A slug that used to belong to this product (before an admin renamed it)
// resolves as `{ redirect: newSlug }` instead of a product — callers should
// navigate to the new slug rather than treat this as "not found".
export function useProduct(slug: string | undefined) {
  return useQuery({
    queryKey: ["product", slug],
    queryFn: () => apiFetch<ProductBySlugResult>(`/products/${slug}`),
    enabled: !!slug
  });
}

export interface ProductSuggestion {
  slug: string;
  name: string;
  imageUrl: string | null;
  basePrice: number;
}

export interface CategorySuggestion {
  slug: string;
  name: string;
}

export interface SearchSuggestions {
  products: ProductSuggestion[];
  categories: CategorySuggestion[];
}

export function useSearchSuggestions(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ["search-suggest", trimmed],
    queryFn: () => apiFetch<SearchSuggestions>(`/products/suggest?q=${encodeURIComponent(trimmed)}`),
    enabled: trimmed.length >= 2,
    staleTime: 30_000
  });
}
